import type { Command } from "../document/commands.ts";
import type { JsonValue, MapDocument, WorldDocument } from "../document/types.ts";
import { waysTo, type Ways } from "./door-links.ts";
import { ownerFields, setThingField, type PickOwner } from "./field-edits.ts";
import type { FieldAddress } from "./field-kinds.ts";
import type { MarkerRef } from "./many-props.ts";
import { declaresReferences, mapReferences, pickRefusal, referenceFieldsOf, referrersOf, thingId, type ReferenceField, type ReferenceLink, type ReferenceThing, type ReferenceTypes } from "./references.ts";
import type { PropRef } from "./templates.ts";

export type { PickOwner } from "./field-edits.ts";

/**
 * The store's side of typed references (ADR-070's amendment "typed references"): Pick on map, Clear, the Links
 * overlay's toggle and the question a delete that would break a reference waits for. Everything held here is
 * view state, never saved or undone; each field it sets is one command through the store, so one undo takes it
 * back. A list of references is picked and cleared row by row (ADR-070's amendment "the other field kinds").
 */

/**
 * A pick in progress: whose field, the field's name, the row of a list it fills (the list's length adds one),
 * its title (a row's with its number) and what it names.
 */
export interface ReferencePick extends ReferenceField {
  readonly owner: PickOwner;
  readonly field: string;
  readonly index?: number;
}

/**
 * A delete held for its question: the things it removes, the links it would break, the doors and rooms' hosts that
 * name an arrival point it removes (ADR-122 decision 2.6), and the delete itself.
 */
export interface PendingDelete {
  readonly things: readonly ReferenceThing[];
  readonly referrers: readonly ReferenceLink[];
  readonly ways: Ways;
  readonly run: () => boolean;
}

/**
 * What the edits need from the store.
 */
export interface ReferenceHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly arrivalType: () => string | undefined;
  readonly types: () => ReferenceTypes;
  readonly run: (command: Command) => boolean;
  readonly setManyField: (name: string, value: JsonValue | null) => boolean;
  readonly propSelection: () => readonly PropRef[];
  readonly markerSelection: () => readonly MarkerRef[];
  readonly isLocked: (layerId: string) => boolean;
  readonly isHidden: (layerId: string) => boolean;
  readonly notify: () => void;
}

/**
 * A selection's ids as one key, so a change of selection is seen.
 *
 * @param props The prop selection.
 * @param markers The marker selection.
 * @returns The key.
 */
function selectionKey(props: readonly PropRef[], markers: readonly MarkerRef[]): string {
  return JSON.stringify([props.map((p) => [p.layerId, p.propId]), markers.map((m) => [m.layerId, m.entityId])]);
}

/**
 * Typed references' edits and view state.
 */
export class ReferenceEdits {
  private active: (ReferencePick & { readonly selection: string }) | null = null;
  private held: PendingDelete | null = null;
  private shown = false;
  private cache: { readonly map: MapDocument; readonly links: readonly ReferenceLink[] } | null = null;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: ReferenceHost) {}

  /**
   * Every reference value on the map, read once per document.
   *
   * @returns The links, each resolved or broken.
   */
  public links(): readonly ReferenceLink[] {
    const map = this.host.map();
    if (this.cache?.map !== map) {
      this.cache = { map, links: mapReferences(map, this.host.types()) };
    }
    return this.cache.links;
  }

  /**
   * The ids of everything a reference field of the map names, sound or broken: what a brush stroke never clears.
   *
   * @returns The ids.
   */
  public referencedIds(): ReadonlySet<string> {
    return new Set(this.links().map((link) => link.id));
  }

  /**
   * The sound links ending on a thing: its Referenced by.
   *
   * @param thing The thing.
   * @returns The links, in document order.
   */
  public referrersOf(thing: ReferenceThing): readonly ReferenceLink[] {
    return referrersOf(this.links(), [thingId(thing)]).filter((link) => link.found?.kind === thing.kind);
  }

  /**
   * The links a thing's own fields hold, sound or broken.
   *
   * @param thing The thing.
   * @returns The links, in its schema's order.
   */
  public linksFrom(thing: ReferenceThing): readonly ReferenceLink[] {
    return this.links().filter((link) => link.owner.kind === thing.kind && thingId(link.owner) === thingId(thing) && link.owner.layerId === thing.layerId);
  }

  /**
   * Whether a type the game registers declares a reference field: the Links overlay is offered only then.
   *
   * @returns True when one does.
   */
  public get offered(): boolean {
    return declaresReferences(this.host.types());
  }

  /**
   * Whether the Links overlay draws every link.
   *
   * @returns The toggle.
   */
  public get overlay(): boolean {
    return this.shown;
  }

  /**
   * Shows or hides every link.
   *
   * @param on Whether to show them.
   */
  public setOverlay(on: boolean): void {
    if (this.shown !== on) {
      this.shown = on;
      this.host.notify();
    }
  }

  /**
   * The pick in progress, or null.
   *
   * @returns The pick.
   */
  public get picking(): ReferencePick | null {
    if (this.active === null) {
      return null;
    }
    const { selection: _selection, ...pick } = this.active;
    return pick;
  }

  /**
   * Why a thing's fields cannot be changed now, or null when they can: its layer is locked or hidden.
   *
   * @param owner The thing, or the selected props.
   * @returns The reason, or null.
   */
  public editReason(owner: PickOwner): string | null {
    const layers = owner.kind === "props" ? [...new Set(this.host.propSelection().map((p) => p.layerId))] : [owner.layerId];
    for (const layerId of layers) {
      const name = this.host.map().layers.find((l) => l.id === layerId)?.name ?? layerId;
      if (this.host.isLocked(layerId)) {
        return `${name} is locked; unlock it to change this field`;
      }
      if (this.host.isHidden(layerId)) {
        return `${name} is hidden; show it to change this field`;
      }
    }
    return null;
  }

  /**
   * Starts picking a field's target on the map: a reference, or one row of a list of references, where the
   * list's length names the row a pick adds.
   *
   * @param owner Whose field.
   * @param field The field's name.
   * @param index The row, for a list.
   * @throws Error when the owner's type declares no reference field of that name, when a list is picked without
   *   a row or a single reference with one, or when the row lies past the list's end.
   */
  public startPick(owner: PickOwner, field: string, index?: number): void {
    const declared = this.fieldOf(owner, field);
    const whose = owner.kind === "props" ? "the selected props'" : `${owner.kind} ${thingId(owner)}'s`;
    if (declared === undefined) {
      throw new Error(`${owner.kind === "props" ? "the selected props have" : `${owner.kind} ${thingId(owner)} has`} no reference field ${field}`);
    }
    if (declared.list === true && index === undefined) {
      throw new Error(`${whose} ${field} is a list; pick a row`);
    }
    if (declared.list !== true && index !== undefined) {
      throw new Error(`${whose} ${field} is not a list`);
    }
    if (index !== undefined && index > this.rowCount(owner, field)) {
      throw new Error(`${field} has no row ${index + 1}`);
    }
    const title = index === undefined ? declared.title : `${declared.title} ${index + 1}`;
    this.active = { owner, field, ...(index === undefined ? {} : { index }), title, target: declared.target, selection: selectionKey(this.host.propSelection(), this.host.markerSelection()) };
    this.host.notify();
  }

  /**
   * Ends picking without changing anything.
   */
  public cancelPick(): void {
    if (this.active !== null) {
      this.active = null;
      this.host.notify();
    }
  }

  /**
   * Fills the picking field with a thing picked on the map: one command when it fits, ending the pick;
   * otherwise nothing is written and picking goes on.
   *
   * @param thing The thing picked.
   * @returns Why it was refused, or null once set.
   */
  public pick(thing: ReferenceThing): string | null {
    const pick = this.active;
    if (pick === null) {
      return "Nothing is being picked";
    }
    const owners: readonly ReferenceThing[] = pick.owner.kind === "props" ? this.host.propSelection().map((p) => ({ kind: "prop", layerId: p.layerId, propId: p.propId })) : [pick.owner];
    for (const owner of owners) {
      const refusal = pickRefusal(this.host.map(), this.host.types(), owner, pick, thing);
      if (refusal !== null) {
        return refusal;
      }
    }
    const reason = this.editReason(pick.owner);
    if (reason !== null) {
      return reason;
    }
    this.active = null;
    if (!this.set(pick.owner, { field: pick.field, ...(pick.index === undefined ? {} : { index: pick.index }) }, thingId(thing))) {
      this.host.notify();
    }
    return null;
  }

  /**
   * Empties a reference field, or removes one row of a list of references, as one command.
   *
   * @param owner Whose field.
   * @param field The field's name.
   * @param index The row, for a list.
   * @returns False when nothing changed or the command was refused.
   */
  public clear(owner: PickOwner, field: string, index?: number): boolean {
    return this.set(owner, { field, ...(index === undefined ? {} : { index }) }, null);
  }

  /**
   * The delete held for its question, or null.
   *
   * @returns The held delete.
   */
  public get pendingDelete(): PendingDelete | null {
    return this.held;
  }

  /**
   * Runs a delete at once when it breaks no reference and removes no arrival point a door or a room names, or holds
   * it for its question, naming the links it would break (those ending on the removed things from things it keeps)
   * and the doors and hosts that lead to the arrival points it removes, which keep their data and turn into broken
   * links (ADR-122 decision 2.6).
   *
   * @param things The things the delete removes.
   * @param run The delete.
   * @returns What the delete returned, or true while it waits for its question.
   */
  public guardDelete(things: readonly ReferenceThing[], run: () => boolean): boolean {
    const referrers = referrersOf(this.links(), things.map(thingId));
    const ways = this.arrivalWays(things);
    if (referrers.length === 0 && ways.doors.length === 0 && ways.hosts.length === 0) {
      return run();
    }
    this.held = { things, referrers, ways, run };
    this.host.notify();
    return true;
  }

  /**
   * The doors and rooms' hosts that name an arrival point among the things a delete removes (ADR-122 decision 2.6),
   * which a delete held here and a layer's deletion both name.
   *
   * @param things The things.
   * @returns What leads to them, in the order the things are given.
   */
  public arrivalWays(things: readonly ReferenceThing[]): Ways {
    const arrivalType = this.host.arrivalType();
    const map = this.host.map();
    const document = this.host.document();
    const doors: Ways["doors"][number][] = [];
    const hosts: Ways["hosts"][number][] = [];
    if (arrivalType === undefined) {
      return { doors, hosts };
    }
    for (const thing of things) {
      if (thing.kind !== "marker") {
        continue;
      }
      const layer = map.layers.find((candidate) => candidate.id === thing.layerId);
      if (layer?.kind !== "entity" || !layer.entities.some((entity) => entity.id === thing.entityId && entity.type === arrivalType)) {
        continue;
      }
      const ways = waysTo(document, map.id, thing.entityId);
      doors.push(...ways.doors);
      hosts.push(...ways.hosts);
    }
    return { doors, hosts };
  }

  /**
   * Answers the question with Delete: runs the held delete as the one command it is.
   *
   * @returns False when nothing was held or the delete was refused.
   */
  public confirmDelete(): boolean {
    const held = this.held;
    if (held === null) {
      return false;
    }
    this.held = null;
    const ran = held.run();
    if (!ran) {
      this.host.notify();
    }
    return ran;
  }

  /**
   * Answers the question with Cancel: nothing is removed.
   */
  public cancelDelete(): void {
    if (this.held !== null) {
      this.held = null;
      this.host.notify();
    }
  }

  /**
   * Lets a pick go once its thing has left the selection or the map, or its field is no longer declared. Called
   * by the store before its subscribers hear of a change.
   */
  public release(): void {
    const pick = this.active;
    if (pick === null) {
      return;
    }
    if (selectionKey(this.host.propSelection(), this.host.markerSelection()) !== pick.selection || this.fieldOf(pick.owner, pick.field) === undefined || (pick.index !== undefined && pick.index > this.rowCount(pick.owner, pick.field))) {
      this.active = null;
    }
  }

  /**
   * The reference field of that name an owner's type declares.
   *
   * @param owner Whose field.
   * @param field The field's name.
   * @returns The field, or undefined when the owner or the field is gone.
   */
  private fieldOf(owner: PickOwner, field: string): (ReferenceField & { readonly list?: true }) | undefined {
    const types = this.host.types();
    const map = this.host.map();
    const schemas = (owner.kind === "props" ? this.host.propSelection().map((p) => ({ kind: "prop" as const, layerId: p.layerId, propId: p.propId })) : [owner]).map((thing) => {
      const layer = map.layers.find((l) => l.id === thing.layerId);
      if (thing.kind === "prop" && layer?.kind === "prop") {
        const type = layer.props.find((p) => p.id === thing.propId)?.behaviour?.type;
        return types.objectTypes.find((t) => t.type === type)?.schema;
      }
      if (thing.kind === "marker" && layer?.kind === "entity") {
        const type = layer.entities.find((e) => e.id === thing.entityId)?.type;
        return types.entityTypes.find((t) => t.type === type)?.schema;
      }
      return undefined;
    });
    const fields = schemas.map((schema) => (schema === undefined ? undefined : referenceFieldsOf(schema).find((f) => f.name === field)));
    const first = fields[0];
    const same = first !== undefined && fields.every((f) => f !== undefined && f.list === first.list && f.target.kind === first.target.kind && f.target.type === first.target.type);
    return same ? { title: first.title, target: first.target, ...(first.list === true ? { list: true as const } : {}) } : undefined;
  }

  /**
   * How many rows a list field of an owner holds (the first selected prop's, for several).
   *
   * @param owner Whose field.
   * @param field The field's name.
   * @returns The rows, 0 when it holds none.
   */
  private rowCount(owner: PickOwner, field: string): number {
    const value = ownerFields(this.host, owner)?.[field];
    return Array.isArray(value) ? value.length : 0;
  }

  /**
   * Sets or clears one reference field, or one row of a list of them, as one command.
   *
   * @param owner Whose field.
   * @param address The field, or one row of it.
   * @param id The target's id, or null to clear it or remove the row.
   * @returns False when nothing changed or the command was refused.
   */
  private set(owner: PickOwner, address: FieldAddress, id: string | null): boolean {
    return setThingField(this.host, owner, address, id);
  }
}
